import { Log, UserManager, WebStorageStateStore } from 'oidc-client';
import SignInCallbackComponent from './SignInCallback';
import SignOutCallbackComponent from './SignOutCallback';
import SilentRenewComponent from './SilentRenew';
import AccessDeniedComponent from './AccessDenied';
import SettingsService from '../settings/settings.service.js';

const getAppBaseUrl = () => {
    let url = document.location.protocol + '//' + document.location.hostname;
    const port = document.location.port;
    if (port > 0 && port !== 80 && port !== 443) {
        url += `:${port}`;
    }
    return url;
}

const isUserAuthenticated = (u) => !!u && !u.expired;

const mgr = new UserManager({
    authority: SettingsService.idsrvUrl,
    client_id: 'web.raporty_travella2',
    scope: 'openid profile api.premia_miesieczna api.travella_v2_header raporty_travella2.web claims_premiamiesieczna',
    response_type: 'code',
    redirect_uri: getAppBaseUrl() + '/sign-in-callback',
    post_logout_redirect_uri: getAppBaseUrl() + '/sign-out-callback',
    silent_redirect_uri: getAppBaseUrl() + '/silent-renew',
    checkSessionInterval: 30 * 1000, // 30sec
    clockSkew: 10 * 60, // 10min
    filterProtocolClaims: true,
    automaticSilentRenew: true,
    monitorSession: true,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: localStorage, prefix: 'oidc.' }),
    acr_values: 'ver:seller'
})

Log.logger = console;
Log.level = SettingsService.isDevelopmentMode ? Log.INFO : Log.ERROR;

var currentAccessToken = '';

mgr.events.addUserLoaded((user) => {
    Log.info('Event user loaded, access token:', user.access_token);
    currentAccessToken = user.access_token;
    authService.invokeAccessTokenChangeHandlers(currentAccessToken);
});

mgr.events.addUserUnloaded(() => {
    Log.info('Event user unloaded');
    currentAccessToken = '';
    authService.invokeAccessTokenChangeHandlers(currentAccessToken);
});

mgr.events.addAccessTokenExpiring((args) => {
    Log.info('Event access token expiring:', args);
});

mgr.events.addAccessTokenExpired((args) => {
    Log.info('Event access token expired:', args);
    currentAccessToken = '';
    authService.invokeAccessTokenChangeHandlers(currentAccessToken);
    mgr.signoutRedirect().then(function(resp) {
        Log.info('User signed out:', resp);
    }).catch(function(err) {
        Log.error('User signed out error:', err);
    })
});

mgr.events.addSilentRenewError((args) => {
    Log.error('Silent renew error:', args);
});

mgr.events.addUserSignedOut((args) => {
    Log.info('Event event user signed out from OP:', args);
    currentAccessToken = '';
    authService.invokeAccessTokenChangeHandlers(currentAccessToken);
    mgr.signoutRedirect().then(function(resp) {
        Log.info('User signed out:', resp);
    }).catch(function(err) {
        Log.error('User signed out error:', err);
    })
});

class AuthService {

    router;
    handlersAccessTokenChange = [];

    get currentAccessToken() {
        return currentAccessToken;
    }

    isSignedInUser() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(false)
                } else {
                    return resolve(true)
                }
            }).catch(function(err) {
                Log.error('Is signed in user error:', err);
                return reject(err)
            });
        })
    }

    signIn(returnPath) {
        const args = {
            state: returnPath
        };
        mgr.signinRedirect(args).then(() => {}).catch(function(err) {
            Log.error('User signed in error:', err);
        });
    }

    signOut() {
        mgr.getUser().then(user => {
            return mgr.signoutRedirect({ id_token_hint: user.id_token }).then(resp => {
                Log.info('User signed out:', resp);
            }).catch(function(err) {
                Log.error('User signed out error:', err);
            });
        });
    }

    endSignin() {
        mgr.signinRedirectCallback().then((user) => {
            if (user) {
                currentAccessToken = user.access_token;
                this.invokeAccessTokenChangeHandlers();
                const redirectUrl = user.state || '/';
                this.router.replace({ path: redirectUrl });
            }
        }).catch((error) => {
            const message = error.message;
            Log.error('User end signed in error:', error);
            if (message.startsWith('State does not match') || message.startsWith('No matching state found')) {
                this.router.replace({ path: '/' });
            } else {
                window.location.href = `${SettingsService.idsrvUrl}/Home/ClientError?ErrorMessage=${message}&ReturnUrl=${getAppBaseUrl()}`;
            }
        });
    }

    endSilentRenew() {
        mgr.signinSilentCallback().then(() => {
            Log.info('End silent renew success');
            this.getAcessToken().then((access_token) => {
                currentAccessToken = access_token;
                this.invokeAccessTokenChangeHandlers();
            });
        }).catch(function(err) {
            Log.error('User end silent renew error:', err);
        });
    }

    endSignout() {
        mgr.signoutRedirectCallback().then((resp) => {
            this.router.replace({ path: '/' });
            Log.info('End signed out success:', resp);
        }).catch(function(err) {
            Log.error('User end signed out error:', err);
        });
    }

    getProfile() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(null)
                } else {
                    return resolve(user.profile)
                }
            }).catch(function(err) {
                Log.error('Get profile error:', err);
                return reject(err)
            });
        })
    }

    getIdToken() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(null)
                } else {
                    return resolve(user.id_token)
                }
            }).catch(function(err) {
                Log.error('Get id token error:', err);
                return reject(err)
            });
        })
    }

    getSessionState() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(null)
                } else {
                    return resolve(user.session_state)
                }
            }).catch(function(err) {
                Log.error('Get session state error:', err);
                return reject(err)
            });
        })
    }

    getAcessToken() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(null)
                } else {
                    return resolve(user.access_token)
                }
            }).catch(function(err) {
                Log.error('Get access token error:', err);
                return reject(err)
            });
        })
    }

    getScopes() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(null)
                } else {
                    return resolve(user.scopes)
                }
            }).catch(function(err) {
                Log.error('Get scopes error:', err);
                return reject(err)
            });
        })
    }

    getRole() {
        return new Promise((resolve, reject) => {
            mgr.getUser().then(function(user) {
                if (!isUserAuthenticated(user)) {
                    return resolve(null)
                } else {
                    return resolve(user.profile.role)
                }
            }).catch(function(err) {
                Log.error('Get role error:', err);
                return reject(err)
            });
        })
    }

    useRouter(router) {
        let self = this;
        self.router = router;
        router.addRoutes(routes);
        router.beforeEach((to, from, next) => {
            const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
            if (requiresAuth) {
                self.isSignedInUser().then(isSignedIn => {
                    if (isSignedIn) {
                        self.getRole().then(
                            sucess => {
                                if (to.meta.role == sucess) {
                                    next();
                                } else {
                                    next('/access-denied');
                                }
                            }
                        );
                    } else {
                        self.signIn(to.path);
                    }
                });
            } else {
                next();
            }
        });
    }

    addAccessTokenChangeHandler(handler) {
        this.handlersAccessTokenChange.push(handler);
    }

    invokeAccessTokenChangeHandlers() {
        this.handlersAccessTokenChange.forEach(h => {
            h(currentAccessToken);
        });
    }
}

const authService = new AuthService();
export default authService;
authService.getAcessToken().then((t) => currentAccessToken = t);

const routes = [{
        path: '/sign-in-callback',
        name: 'SignInCallback',
        component: SignInCallbackComponent,
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/sign-out-callback',
        name: 'SignOutCallback',
        component: SignOutCallbackComponent,
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/silent-renew',
        name: 'SilentRenew',
        component: SilentRenewComponent,
        meta: {
            requiresAuth: false
        }
    },
    {
        path: '/access-denied',
        name: 'AccessDenied',
        component: AccessDeniedComponent,
        meta: {
            requiresAuth: false
        }
    },
];